import React from 'react';
import styled from 'libs/styled';
import { above } from '@fogia/core-web/utils/mediaqueries';
import Text from 'components/Text';
import { show as showOverlay } from '@fogia/core-web/state/application/overlay';
import useAppDispatch from '@fogia/core-web/hooks/useAppDispatch';
import useAppSelector from '@fogia/core-web/hooks/useAppSelector';
import Link from '@fogia/core-web/components/Link';
import { getSlugFromInternalRef } from '@fogia/core-web/utils/internalRefToSlug';
import { useHeaderNavigationContext } from '@fogia/core-web/contexts/HeaderNavigationContext';
import ImageSlider, { type ImageSliderImage } from '../ImageSlider';

interface Dimensions {
    height?: number | string;
    width?: number | string;
    depth?: number | string;
}

export interface BelowFoldProps {
    productName?: string;
    designerName?: string;
    description?: string;
    dimensions?: Dimensions;
    lineDrawings?: string[];
    images?: ImageSliderImage[];
    year?: string | number;
    imageSliderHeading?: string;
    imageSliderText?: string;
}

const Wrapper = styled('div')`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const AboutSection = styled('div')`
    display: flex;
    flex-direction: column;
    padding: 40px 0;
    gap: 10px;

    ${above['tablet.lg']} {
        flex-direction: row;
        justify-content: space-between;
    }
`;

const DimensionsSection = styled('div')`
    display: flex;
    flex-direction: column;
    border-top: 0.5px solid var(--stroke-default, rgba(23, 23, 23, 0.15));
    padding: 40px 0;
    padding-top: 10px;
    gap: 10px;

    ${above['tablet.lg']} {
        flex-direction: row;
        justify-content: space-between;
    }
`;

const SectionLabel = styled('div')`
    width: 100%;

    ${above['tablet.lg']} {
        flex: 1;
        flex-shrink: 0;
    }
`;

const SectionContent = styled('div')`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const LinksGrid = styled('div')`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
`;

const LinkButton = styled('button')<{ disabled?: boolean }>`
    background: none;
    border: none;
    padding: 5px 0;
    cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
    text-align: left;
    opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};

    &:hover {
        opacity: ${({ disabled }) => (disabled ? 0.4 : 0.7)};
    }
`;

const LineDrawingImageContainer = styled('div')`
    display: flex;
`;

const LineDrawingImageWrapper = styled('button')`
    aspect-ratio: 16/9;
    object-fit: contain;
    width: 50%;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    transition: opacity var(--transition-primary-fast);

    &:hover {
        opacity: 0.7;
    }
`;

const LineDrawingImage = styled('img')`
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: left;
`;

const BelowFold = ({
    productName = '',
    designerName = '',
    description = '',
    lineDrawings,
    images = [],
    year,
    imageSliderHeading = '',
    imageSliderText = '',
}: BelowFoldProps) => {
    const dispatch = useAppDispatch();
    const { headerNavigationState, setHeaderNavigationState } = useHeaderNavigationContext();
    const sitePages = useAppSelector((state) => state.application?.config?.siteOptions?.pages);
    const references = useAppSelector((state) => state.application?.references) ?? {};

    const referenceCasesPageUrl = sitePages?.referenceCasesPage?.ref
        ? getSlugFromInternalRef(sitePages.referenceCasesPage.ref, references)
        : undefined;

    const handleOpenOverlay = (initialTab: string) => {
        dispatch(showOverlay({ view: 'productOverlay', transition: 'from-right', data: { initialTab } }));
    };

    return (
        <Wrapper>
            {/* About section */}
            <AboutSection>
                <SectionLabel>
                    <Text typography="Primary/15_120_510_-1" m="0">
                        About
                    </Text>
                </SectionLabel>
                <SectionContent>
                    {description && (
                        <Text
                            typography={['Secondary/Mobile/18_120_400_-1', null, null, 'Secondary/20_120_400_-1']}
                            m="0"
                            dangerouslySetInnerHTML={{ __html: description }}
                        >
                            {null}
                        </Text>
                    )}
                    <LinksGrid>
                        <LinkButton onClick={() => handleOpenOverlay('product-details')}>
                            <Text
                                typography="Primary/15_120_510_-1"
                                m="0"
                                textDecoration="underline"
                                textUnderlinePosition="from-font"
                            >
                                Product Details
                            </Text>
                        </LinkButton>
                        <LinkButton
                            as={referenceCasesPageUrl ? Link : 'button'}
                            to={referenceCasesPageUrl}
                            disabled={!referenceCasesPageUrl}
                        >
                            <Text
                                typography="Primary/15_120_510_-1"
                                m="0"
                                textDecoration="underline"
                                textUnderlinePosition="from-font"
                            >
                                Reference cases
                            </Text>
                        </LinkButton>
                        <LinkButton onClick={() => handleOpenOverlay('downloads')}>
                            <Text
                                typography="Primary/15_120_510_-1"
                                m="0"
                                textDecoration="underline"
                                textUnderlinePosition="from-font"
                            >
                                Downloads
                            </Text>
                        </LinkButton>
                        <LinkButton
                            onClick={() =>
                                setHeaderNavigationState({
                                    ...headerNavigationState,
                                    storeFinderActive: true,
                                    mobileMenuActive: false,
                                    desktopMenuActive: false,
                                    searchActive: false,
                                })
                            }
                        >
                            <Text
                                typography="Primary/15_120_510_-1"
                                m="0"
                                textDecoration="underline"
                                textUnderlinePosition="from-font"
                            >
                                Find dealer
                            </Text>
                        </LinkButton>
                    </LinksGrid>
                </SectionContent>
            </AboutSection>

            {/* Dimensions section */}
            {lineDrawings && lineDrawings.length > 0 && (
                <DimensionsSection>
                    <SectionLabel>
                        <Text typography="Primary/15_120_510_-1" m="0">
                            Dimensions
                        </Text>
                    </SectionLabel>
                    <SectionContent>
                        <LineDrawingImageContainer>
                            {lineDrawings.map((src, index) => (
                                <LineDrawingImageWrapper
                                    key={src}
                                    onClick={() =>
                                        dispatch(
                                            showOverlay({
                                                view: 'zoomGalleryOverlay',
                                                transition: 'fade',
                                                data: {
                                                    images: lineDrawings.map((url, i) => ({
                                                        id: `line-drawing-${i}`,
                                                        url,
                                                        title: `${productName} dimensions ${i + 1}`,
                                                    })),
                                                    startIndex: index,
                                                    productName,
                                                },
                                            }),
                                        )
                                    }
                                >
                                    <LineDrawingImage src={src} alt={`${productName} dimensions ${index + 1}`} />
                                </LineDrawingImageWrapper>
                            ))}
                        </LineDrawingImageContainer>
                    </SectionContent>
                </DimensionsSection>
            )}

            {images.length > 0 && (
                <ImageSlider images={images} productName={imageSliderHeading} text={imageSliderText} />
            )}
        </Wrapper>
    );
};

export default BelowFold;
